import { cancel, select, takeEvery } from 'redux-saga/effects';
import {
  DruckRequestPayload,
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  mapLeistungen,
  mapToBffVorvertragsdatenDruck,
  mapZusatzoptionenDruck,
  numberToGermanNumberFormattedString
} from '@ps20-frontend/frontend-models';
import { Feature, SpartenType, VorschaedenDruck, VwgV2Druckdaten, ZusatzoptionenDruck } from '@ps20/bff-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { getProduktlinienDruck, mapGefahren, mapLinienpreis } from '@ps20-frontend/druckdaten-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { mapGesamtbeitragIfJaehrlichAndBeitragIsWrong } from '@ps20-frontend/preis-lib';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import {
  selectSelectedProduktlinie,
  selectVersicherteNebengebaeudeDeselected,
  selectZusatzoptionItems
} from '../../app/shared/selectors/produkt.selector';
import { isNebengebaeudeLeistungSelectable } from '../../app/shared/selectors/gebaeude.selector';
import { selectVorschaeden } from '../../app/shared/selectors/vorschaeden.selector';
import { Vorschaeden } from '../reducers/vorschaeden.reducer';
import { IAppState } from '../appState';
import { selectIsErsatzgeschaeft } from '../../app/shared/selectors/vertragsoption.selector';
import { resolveOutdoorPool } from './transformer/vwgData.transformer';

export function* watchDruckdatenChanged() {
  yield takeEvery(GlobalActions.DRUCKDATEN_REQUESTED, safeSaga(onDruckdatenRequested, App.VWG_V2));
}

export function* onDruckdatenRequested(action: GlobalAction<DruckRequestPayload>) {
  try {
    const state: IAppState = yield select();
    const appIds: string[] = action.payload.appIds;

    if (!appIds.includes(App.VWG_V2)) {
      yield cancel();
      return;
    }

    const vwgV2Druckdaten: VwgV2Druckdaten = mapDruckdaten(state);
    GlobalPut.getInstance().put(new GlobalActions().druckdatenGenerated(vwgV2Druckdaten));
  } catch (e) {
    PS20Logger.error(App.VWG_V2, e as Error, 'Druckdatensaga.onDruckdatenRequested()');
    yield cancel();
  }
}

function mapDruckdaten(state: IAppState): VwgV2Druckdaten {
  const vorschaeden: Vorschaeden = selectVorschaeden(state);
  const zusatzoptionenDruck: Array<ZusatzoptionenDruck> = mapZusatzoptionenDruck(
    selectZusatzoptionItems(state)
  );
  const ausgewaehlteProduktlinie = selectSelectedProduktlinie(state);
  const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);
  const outbuildingValid = isNebengebaeudeLeistungSelectable(state);
  return {
    angebotsId: state.angebotsId,
    antragsnummer: state.antragsnummer,
    longText: ausgewaehlteProduktlinie.langtext,
    shortText: ausgewaehlteProduktlinie.kurztext,
    rbdAktuell: state.taasInfo.rbdNachlass,
    treuebonus: state.taasInfo.tbNachlass,
    vorteilsRabatt: vorteilsRabattActivated ? state.taasInfo.vorteilsrabatt || 0 : undefined,
    angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
    zaehlstuecke: state.rabattstufe,
    spartenType: SpartenType.WOHNGEBAEUDE_V2,
    beginn: null, // kommt vom Checkout
    ablauf: null, // kommt vom Checkout
    gesamtbeitrag: numberToGermanNumberFormattedString(
      mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
        state.vertragsoptionen.zahlungsweise,
        state.gesamtbeitragMitRabatt,
        state.jahresbeitrag,
        App.VWG_V2,
      ),
    ),
    selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
    vertragsdauer: null,
    linienpreis: mapLinienpreis(state.produkt),
    leistungen: mapLeistungen(ausgewaehlteProduktlinie, SpartenType.WOHNGEBAEUDE_V2),
    zusatzoptionenDruck,
    vorschaedenDruck: mapVorschaeden(vorschaeden, selectIsErsatzgeschaeft(state)),
    vorversicherungenDruck: null,
    gefahrenDruck: mapGefahren(state.produkt),
    produktlinien: getProduktlinienDruck(state.produkt),
    zuersGefaehrdungsklasse:
      state.gisDaten.zuersGefaehrdungsklasse != null
        ? state.gisDaten.zuersGefaehrdungsklasse.toString()
        : '',
    vorvertragsnummern: mapToBffVorvertragsdatenDruck(state.vertragsoptionen.vorvertragsNummern),
    vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
    abweichungVomZielmarkt: false,
    beibehalten: state.vertragsoptionen.beibehalten,
    gebaeudeDruck: {
      adresse: {
        plz: state.risikoort.plz,
        ort: state.risikoort.ort,
        strasse: state.risikoort.strasse,
        hausnummer: state.risikoort.hausnummer,
        longtitude: state.risikoort.longtitude,
        latitude: state.risikoort.latitude,
      },
      gebkategorie: state.buildingFeatures.buildingCategory,
      baujahr: state.gebaeude.baujahr,
      wohnflaecheqm: state.gebaeude.wohnflaeche,
      kellerVorhanden: state.buildingFeatures.basement,
      kellerMitWohnflaeche: mapKellerMitWohnflaeche(state),
      leerstehend: state.buildingFeatures.notPermanentlyOccupied,
      dachbelag: state.buildingFeatures.softRoofCovering,
      holzhaus: state.buildingFeatures.woodenHouse,
      solarthermie: state.buildingFeatures.solarThermal,
      photovoltaik: state.buildingFeatures.photovoltaic,
      flachdach: state.buildingFeatures.flatRoof,
      geothermie: state.buildingFeatures.geothermal,
      ferienhaus: state.buildingFeatures.vacationHome,
      schwimmbad: state.buildingFeatures.pool,
      gasheizung: state.buildingFeatures.gasHeating,
      oelheizung: state.buildingFeatures.oilHeating,
      fernwaerme: state.buildingFeatures.districtHeating,
      holzheizung: state.buildingFeatures.pelletHeating,
      vermietung: state.buildingFeatures.rented,
      schwimmbadInnen: state.buildingFeatures.poolIndoors,
      schwimmbadAussen: resolveOutdoorPool(state),
      outbuildingSizeCategory: outbuildingValid ? state.gebaeude.nebengebaeude.sizeCategory : null,
      denkmalschutz: state.buildingFeatures.heritageProtection,
      nebengebaeude: outbuildingValid ? state.gebaeude.nebengebaeude.available === true : false,
      nebengebaeudeDeselected: outbuildingValid
        ? selectVersicherteNebengebaeudeDeselected(state) || false
        : false,
      nebengebaeudeqm: outbuildingValid ? state.gebaeude.nebengebaeude.totalArea : null,
      feuerrohbau: state.buildingFeatures.fireShellConstruction,
      gleitenderNeuwert: state.gisDaten.gleitenderNeuwert,
    },
    sicherungsglaeubigerDruck: [], // kommt vom Checkout,
    extremwetterschutzSelbstbeteiligung: state.extremeWetterschutzSelbstbeteiligung,
    kooperation: state.vertragsoptionen.selectedKooperation,
    geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
  };
}

function mapKellerMitWohnflaeche(state: IAppState): boolean {
  return state.buildingFeatures.basementWithLivingSpace === null ? false : state.buildingFeatures.basementWithLivingSpace;
}

function countIfIncluded(entry: { includeInDruck: boolean; count: number }): number {
  return entry.includeInDruck ? entry.count : 0;
}

function mapVorschaeden(vorschaden: Vorschaeden, isErsatzgeschaeft: boolean): VorschaedenDruck {
  const visibleInErsatzgeschaeftByKey = new Map([
    ['elementar', vorschaden.elementarVorschaden.visibleInErsatzgeschaeft],
    ['feuer', vorschaden.feuerVorschaden.visibleInErsatzgeschaeft],
    ['leitungswasser', vorschaden.leitungswasserVorschaden.visibleInErsatzgeschaeft],
    ['sturmhagel', vorschaden.sturmhagelVorschaden.visibleInErsatzgeschaeft],
  ]);
  const isVisible = (key: string) => !isErsatzgeschaeft || visibleInErsatzgeschaeftByKey.get(key);
  return {
    vorhanden: true,
    elementar: isVisible('elementar')
      ? countIfIncluded(vorschaden.elementarVorschaden)
      : undefined,
    feuer: isVisible('feuer')
      ? countIfIncluded(vorschaden.feuerVorschaden)
      : undefined,
    leitungswasser: isVisible('leitungswasser')
      ? countIfIncluded(vorschaden.leitungswasserVorschaden)
      : undefined,
    sturmhagel: isVisible('sturmhagel')
      ? countIfIncluded(vorschaden.sturmhagelVorschaden)
      : undefined,
  };
}
